// The live floor and the station drawer's live-ops (spec/13 §3, spec/09 §4-§5; F-STN-14 … F-STN-18).
//
//   GET  /v1/organizations/{o}/venues/{v}/floor                        the floor snapshot   floor:read
//   POST /v1/organizations/{o}/venues/{v}/stations/{stationId}/power   restart/…/wake       stations:power
//   POST /v1/organizations/{o}/venues/{v}/stations/{stationId}/lock    lock / unlock        sessions:lock
//   POST …/stations/{stationId}/maintenance                            maintenance toggle   stations:maintenance
//   POST …/stations/{stationId}/setup/skip                             skip start-up setup  stations:maintenance
//
// **Snapshot, then stream (spec/09 §4).** The console subscribes to `venue:{id}:floor`, buffers the
// `floor.station.updated` deltas, fetches this snapshot (which returns the channel's `version`), and
// applies every buffered or later event whose `version` is greater. So the snapshot carries the same
// live per-station state the delta does — online, mode, what it is running — from the realtime tier's
// presence (spec/09 §8), not from a Postgres write per report. The station's configuration (its class,
// zone, capacity, number) is on the tile too, so a console draws it without a second read.
//
// The tile's session, player, funded-time ring, calls, reservations and health join it additively with
// the features that own them (sessions F-SES-01, calls F-RES, reservations F-RES-08) — an addition to
// this shape, never a break, exactly as `FloorStationUpdatedPayload` says of the delta.
//
// **The drawer's commands are issued through the gateway (spec/09 §5.1).** Each POST writes a
// `station_command` row, publishes it on `station:{id}`, and answers `202` with the command — its id,
// its type and its `queued`/`sent` status — so the console can follow it to "done" over the issuing
// member's `command.updated` (spec/09 §6.3). It never blocks on the station's ack: an offline station
// runs the command on its next connect, until the command expires (spec/09 §6.2).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, StationClassIdSchema, StationCommandIdSchema, StationIdSchema, VenueIdSchema, ZoneIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { OFFLINE_CODE_PATTERN } from "../primitives/offline-code.js";
import { StationCommandStatusSchema, StationCommandTypeSchema } from "../realtime/commands.js";
import { OfflineCodeActionSchema, OfflineCodeMinutesBucketSchema, StationAlertKindSchema, StationAlertSeveritySchema, StationModeSchema } from "../realtime/station.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { FLOOR_READ, SESSIONS_LOCK, SESSIONS_OFFLINE_CODE, STATIONS_MAINTENANCE, STATIONS_MAINTENANCE_IN_SESSION, STATIONS_MESSAGE, STATIONS_POWER, } from "../permissions/index.js";
import { OTHERS_PERMISSION_EXTENSION, PERMISSION_EXTENSION } from "./audit.js";
import { StationClientKindSchema, StationKindSchema, StationStatusSchema } from "./stations.js";
// ---------------------------------------------------------------------------------------------
// The snapshot (F-STN-14)
// ---------------------------------------------------------------------------------------------
/**
 * A tile's health badge (F-STN-11, spec/13 §3.1): how many alerts are open on the station and the loudest of
 * them. Read from the open `station_alert` rows on every snapshot, so a cleared alert takes its badge with it
 * without a manual dismiss.
 */
export const FloorStationHealthSchema = z
    .object({
    /** How many alerts are open. `0` is a healthy tile: no badge. */
    openAlerts: z.int().min(0),
    /** The loudest open alert's severity, or `null` with none open. */
    severity: StationAlertSeveritySchema.nullable(),
    /** The open kinds, for the badge's tooltip. */
    kinds: z.array(StationAlertKindSchema),
})
    .openapi('FloorStationHealth', { description: 'A floor tile’s health badge: its open alerts and the loudest severity (F-STN-11).' });
/**
 * What a station is running now, on its floor tile (F-CAT-04, spec/10 §7.3): the library title being played
 * (`titleId` and `name` set) or, when none is, the foreground program (`titleId` null — "Other: chrome"), and since
 * when. The same shape as the realtime `StationForeground` that `floor.station.updated` carries; named apart so the
 * station's generated HTTP and realtime DTOs do not both declare one record.
 */
export const FloorStationTitleSchema = z
    .object({
    /** The catalogue game (`gam_…`) or custom app (`app_…`) being played, or `null` for another program. */
    titleId: z.string().min(1).max(64).nullable(),
    /** The process, lower case, without `.exe`. */
    exe: z.string().min(1).max(260),
    /** The title's name, when it is a library title. */
    name: z.string().min(1).max(200).optional(),
    /** When the play (or the foreground program) started. */
    since: IsoDateTimeSchema.optional(),
})
    .openapi('FloorStationTitle', { description: 'What a station is running now: the title being played, else the foreground program (F-CAT-04).' });
/**
 * A station's start-up setup on its floor tile (ADR-0055, F-STN-26): while it is `setting_up`, installing the apps the
 * venue marked "Install for players" before it opens for play — "Setting up · 2 of 3". The same fields as the realtime
 * `StationSetupProgress` that `station.status` and `floor.station.updated` carry, named apart (as `FloorStationTitle`
 * is) so the station's generated HTTP and realtime DTOs do not both declare one record.
 */
export const FloorStationSetupSchema = z
    .object({
    /** The apps this setup installs. */
    total: z.int().min(1).max(100),
    /** How many are done: installed, or skipped because their install failed. */
    done: z.int().min(0).max(100),
    /** How many of the done were skipped. */
    skipped: z.int().min(0).max(100),
    /** The app installing now, or `null` between two apps. */
    current: z.string().min(1).max(200).nullable(),
    /** When the setup began. */
    startedAt: IsoDateTimeSchema,
    /** When its time limit opens the PC for play whatever is left. */
    endsBy: IsoDateTimeSchema,
})
    .openapi('FloorStationSetup', { description: 'A station’s start-up setup: how many of its apps are done, and which installs now (ADR-0055).' });
/**
 * One station's tile on the live floor: its configuration, and its live state from presence.
 *
 * `online` is whether the station holds a socket now; `mode` and `lastSeenAt` are `null` until it has
 * reported them (an offline station has no live mode). A console colours the tile by the class
 * (`classId`) and groups it by class or zone (`classId`, `zoneId`), which it already holds from the
 * classes and zones lists — the snapshot carries the ids, not a copy of their names and colours.
 *
 * `title` is what the station is running now — the library title being played, else the foreground
 * program — with its name and since when (F-CAT-04, spec/10 §7.3): the same `{ titleId, exe, name?, since? }`
 * `floor.station.updated` carries (spec/09 §6.3), read from presence, so a console opened mid-game shows
 * "Playing Dota 2 · 23 min" before the next delta. The session, player, funded-time ring, calls and health
 * join the same way, with the features that own them.
 */
export const FloorStationStateSchema = z
    .object({
    stationId: StationIdSchema,
    number: z.int().min(1),
    /** An optional label; null when the station goes by its number alone. */
    name: z.string().nullable(),
    kind: StationKindSchema,
    classId: StationClassIdSchema,
    /** The zone the station is placed in, or null when it is unplaced. */
    zoneId: ZoneIdSchema.nullable(),
    capacity: z.int().min(1),
    /** What staff set: `active`, or `disabled` ("out of order" on the kiosk). Distinct from `mode`. */
    status: StationStatusSchema,
    disabledReason: z.string().nullable(),
    /**
     * Managed (a PC with the station app) or staff-run (ADR-0033). A staff-run station holds no socket, so it is never
     * `online`, and is never offline either: its `mode` comes from its session, not from presence.
     */
    managed: z.boolean(),
    /**
     * Which app the PC runs (ADR-0065): `station`, or `none` — a Clavian Boot only PC, never online, never paired and
     * never in a session, whose tile shows only how it starts. Optional, an addition: absent is `station`.
     */
    client: StationClientKindSchema.optional(),
    /** Whether the station holds a realtime socket now (spec/09 §8). Always false for a staff-run station. */
    online: z.boolean(),
    /**
     * Whether a PC is paired to the station (a device is bound, F-STN-02), online or not; always false for a staff-run
     * station. What the station window offers Unpair on (ADR-0057). Optional, an addition.
     */
    paired: z.boolean().optional(),
    /**
     * Its last reported runtime mode (spec/10 §6.1), or `null` before it has reported one. A staff-run station's is the
     * api's: `in_session` or `session_locked` while a session runs on it, else its stored mode (`locked` when free). In
     * the snapshot a managed station's is reconciled the same way (2026-10-07): one that reports `locked` — or nothing
     * yet, just reconnected — while the cloud runs its session reads `in_session` (`session_locked` if that session is
     * locked), never free.
     */
    mode: StationModeSchema.nullable(),
    /** When the station last reported anything, or `null` when it never has (spec/09 §5.3). */
    lastSeenAt: IsoDateTimeSchema.nullable(),
    /** The tile's health badge (F-STN-11). */
    health: FloorStationHealthSchema,
    /** What it is running now (the title being played, else the foreground program), or `null` — offline, or nothing (F-CAT-04). */
    title: FloorStationTitleSchema.nullable(),
    /**
     * The start-up setup's progress while the station is `setting_up` (ADR-0055, F-STN-26), from presence; `null` or
     * absent otherwise. Optional, an addition.
     */
    setup: FloorStationSetupSchema.nullable().optional(),
})
    .openapi('FloorStationState', {
    description: 'One station tile on the live floor: its configuration and its live presence (spec/13 §3, spec/09 §6.3).',
});
export const FloorSnapshotSchema = z
    .object({
    /**
     * The `venue:{id}:floor` channel's version at this snapshot (spec/09 §4). The console applies only
     * the events whose `version` is greater. A venue with no live floor traffic yet reads `0`.
     */
    version: z.int().min(0),
    /** Every station at the venue, oldest first, with its live state. */
    stations: z.array(FloorStationStateSchema),
})
    .openapi('FloorSnapshot', {
    description: 'The live floor as one read: the version to apply events past, and every station with its live state (F-STN-14).',
});
// ---------------------------------------------------------------------------------------------
// The command view — what a drawer action answers
// ---------------------------------------------------------------------------------------------
/**
 * A station command the api has written and published (spec/09 §5.1). `status` is `queued` or `sent`
 * when it answers; the console follows it to `acked`, `failed` or `expired` over the issuing member's
 * `command.updated` (spec/09 §6.3).
 */
export const StationCommandViewSchema = z
    .object({
    id: StationCommandIdSchema,
    stationId: StationIdSchema,
    type: StationCommandTypeSchema,
    status: StationCommandStatusSchema,
    createdAt: IsoDateTimeSchema,
    /** When it stops being worth delivering (spec/09 §6.2). */
    expiresAt: IsoDateTimeSchema,
})
    .openapi('StationCommandView', {
    description: 'A station command the api issued through the gateway, for the console to follow to done (spec/09 §5.1).',
});
// ---------------------------------------------------------------------------------------------
// The drawer's request bodies (F-STN-16 … F-STN-18)
// ---------------------------------------------------------------------------------------------
/** The power actions a console offers (spec/13 §3.3, F-STN-17). `wake` is relayed by Wake-on-LAN. */
export const STATION_POWER_ACTIONS = ['restart', 'shutdown', 'sleep', 'wake'];
export const StationPowerActionSchema = z.enum(STATION_POWER_ACTIONS).openapi('StationPowerAction', {
    description: 'What to do to a station: restart, shut it down, sleep it, or wake it (spec/09 §6.2).',
});
export const StationPowerRequestSchema = z
    .object({
    action: StationPowerActionSchema,
    /**
     * Seconds to warn a player in session before acting (spec/09 §6.2 `station.power.delaySeconds`).
     * Ignored for `wake`. Omit for the command's own default.
     */
    delaySeconds: z.int().min(0).max(600).optional(),
    /** Act even if a session is running, after the warning. Ignored for `wake`. */
    force: z.boolean().optional(),
})
    .strict()
    .openapi('StationPowerRequest');
export const StationLockRequestSchema = z
    .object({
    /** `true` locks the station's screen (`session.lock`), `false` unlocks it (`session.unlock`). */
    locked: z.boolean(),
})
    .strict()
    .openapi('StationLockRequest');
export const StationMaintenanceRequestSchema = z
    .object({
    /** `true` puts the station into maintenance mode (`station.mode.set` `maintenance`); `false` takes it out (`locked`). */
    enabled: z.boolean(),
    /**
     * How long maintenance lasts, in minutes (spec/13 §3.3 "Duration"). Applies only when `enabled`;
     * omit for the venue's default maintenance length. The station leaves maintenance when it elapses.
     */
    durationMinutes: z.int().min(1).max(24 * 60).optional(),
})
    .strict()
    .openapi('StationMaintenanceRequest');
export const StationMessageRequestSchema = z
    .object({
    /** The line to show the player at the station — a kiosk toast and the in-game overlay (spec/05 §7). */
    text: z.string().min(1).max(280),
    /** Ask the player to acknowledge it before it clears (spec/13 §3.3 "needs acknowledgement"). Defaults to false. */
    needsAck: z.boolean().optional(),
})
    .strict()
    .openapi('StationMessageRequest');
// ---------------------------------------------------------------------------------------------
// Offline codes (F-SES-24, spec/05 §8.4)
// ---------------------------------------------------------------------------------------------
export const OfflineCodeRequestSchema = z
    .object({
    /** Start a session, extend the running one, or unlock the station for maintenance (spec/05 §8.4). */
    action: OfflineCodeActionSchema,
    /** How many minutes the code carries — one of the fixed buckets (spec/05 §8.4). */
    minutesBucket: OfflineCodeMinutesBucketSchema,
})
    .strict()
    .openapi('OfflineCodeRequest');
/**
 * A minted offline code (F-SES-24). The `code` is the 8 digits staff read out; the station accepts it
 * for the time step it was minted in and the one before (about 10–20 minutes), so `expiresAt` is the
 * end of the last step that accepts it. The station's per-station secret never leaves the server.
 */
export const OfflineCodeSchema = z
    .object({
    code: z.string().regex(OFFLINE_CODE_PATTERN).openapi({ description: 'The 8-digit code staff read out (spec/05 §8.4).', example: '01234567' }),
    action: OfflineCodeActionSchema,
    minutesBucket: OfflineCodeMinutesBucketSchema,
    /** The 10-minute window the code was minted in (`floor(unixSeconds / 600)`, spec/05 §8.4). */
    timeStep: z.int().min(0),
    /** When the last time step that accepts this code ends — after this the station refuses it (spec/05 §8.4). */
    expiresAt: IsoDateTimeSchema,
})
    .openapi('OfflineCode', {
    description: 'An 8-digit offline code for a station and action, valid for its time step and the previous one (F-SES-24).',
});
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
export const StationActionParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, venueId: VenueIdSchema, stationId: StationIdSchema })
    .openapi('StationActionParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const venueNotFound = problemResponse('No such venue in this organisation, or not one you can see.', 'not-found');
const stationNotFound = problemResponse('No such station at this venue.', 'not-found');
const commandUnavailable = problemResponse('The gateway is not available to deliver commands right now.', 'internal-error');
export const getFloorRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/floor',
    operationId: 'getFloor',
    tags: ['stations'],
    summary: 'The live floor snapshot',
    description: 'Every station at the venue with its live state, and the `venue:{id}:floor` version to apply events past ' +
        '(spec/09 §4). Requires `floor:read`.',
    [PERMISSION_EXTENSION]: FLOOR_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: { description: 'The live floor.', content: { [JSON_CONTENT_TYPE]: { schema: FloorSnapshotSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: venueNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const stationPowerRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/power',
    operationId: 'stationPower',
    tags: ['stations'],
    summary: 'Power a station',
    description: 'Restart, shut down, sleep or wake a station (spec/13 §3.3, F-STN-17). Issued as a command through the ' +
        'gateway; a station in session is warned and delayed. Requires `stations:power`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_POWER,
    request: {
        params: StationActionParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: StationPowerRequestSchema } }, required: true },
    },
    responses: {
        202: { description: 'The command was written and published.', content: { [JSON_CONTENT_TYPE]: { schema: StationCommandViewSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        409: problemResponse('The station cannot be woken now.', 'wake-unavailable'),
        429: commonProblemResponses[429],
        500: commandUnavailable,
        503: commonProblemResponses[503],
    },
});
export const lockStationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/lock',
    operationId: 'lockStation',
    tags: ['stations'],
    summary: 'Lock or unlock a station',
    description: 'Lock a station’s screen, or unlock it, from the floor (spec/13 §3.3, F-STN-16). Issued as `session.lock` / ' +
        '`session.unlock` through the gateway. Requires `sessions:lock`. Audited.',
    [PERMISSION_EXTENSION]: SESSIONS_LOCK,
    request: {
        params: StationActionParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: StationLockRequestSchema } }, required: true },
    },
    responses: {
        202: { description: 'The command was written and published.', content: { [JSON_CONTENT_TYPE]: { schema: StationCommandViewSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commandUnavailable,
        503: commonProblemResponses[503],
    },
});
export const generateOfflineCodeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/offline-codes',
    operationId: 'generateOfflineCode',
    tags: ['stations'],
    summary: 'Generate an offline code for a station',
    description: 'Mint the 8-digit code staff read out to start, extend or run maintenance on a station while its venue is ' +
        'offline (spec/05 §8.4, F-SES-24). Derived from the station’s per-station offline secret; the code is ' +
        'returned but the secret never is. Requires `sessions:offline_code`. Audited.',
    [PERMISSION_EXTENSION]: SESSIONS_OFFLINE_CODE,
    request: {
        params: StationActionParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: OfflineCodeRequestSchema } }, required: true },
    },
    responses: {
        200: { description: 'The minted offline code.', content: { [JSON_CONTENT_TYPE]: { schema: OfflineCodeSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        409: problemResponse('This station cannot generate offline codes: it has no offline secret (paired before F-SES-24).', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const messageStationRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/message',
    operationId: 'messageStation',
    tags: ['stations'],
    summary: 'Message a player at a station',
    description: 'Send a short line to the player at a station from the floor (spec/13 §3.3, F-STN-20), optionally requiring ' +
        'an acknowledgement. Issued as a `session.message` command through the gateway; the station shows it to whoever ' +
        'is on it. Requires `stations:message`. Audited.',
    [PERMISSION_EXTENSION]: STATIONS_MESSAGE,
    request: {
        params: StationActionParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: StationMessageRequestSchema } }, required: true },
    },
    responses: {
        202: { description: 'The command was written and published.', content: { [JSON_CONTENT_TYPE]: { schema: StationCommandViewSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commandUnavailable,
        503: commonProblemResponses[503],
    },
});
export const setStationMaintenanceRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/maintenance',
    operationId: 'setStationMaintenance',
    tags: ['stations'],
    summary: 'Put a station into or out of maintenance',
    description: 'Put a station into maintenance mode for a duration, or take it out (spec/13 §3.3, F-STN-18). Issued as ' +
        '`station.mode.set` through the gateway. Requires `stations:maintenance`; while a player session runs on the ' +
        'station it also requires `stations:maintenance_in_session` (else `409 conflict`), and the player is warned ' +
        '(spec/11 §7, F-LCK-09). Audited.',
    [PERMISSION_EXTENSION]: STATIONS_MAINTENANCE,
    // Maintenance during a live session is the player's session being interrupted: an extra permission on top of the
    // route's own (F-LCK-09), declared like `shifts:close_any` so the registry counts it as gating this route.
    [OTHERS_PERMISSION_EXTENSION]: STATIONS_MAINTENANCE_IN_SESSION,
    request: {
        params: StationActionParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: StationMaintenanceRequestSchema } }, required: true },
    },
    responses: {
        202: { description: 'The command was written and published.', content: { [JSON_CONTENT_TYPE]: { schema: StationCommandViewSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        429: commonProblemResponses[429],
        500: commandUnavailable,
        503: commonProblemResponses[503],
    },
});
/**
 * "Skip setup" (F-STN-26, ADR-0055; 2026-10-11): end a PC's start-up setup and open it for play now. A PC with
 * `stations.setupBeforePlay` on that starts while apps marked "Install for players" are missing on it is `setting_up`
 * until they are installed, an install fails (that app is skipped), or its time limit ends it; staff may end it sooner.
 */
export const skipStationSetupRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/stations/{stationId}/setup/skip',
    operationId: 'skipStationSetup',
    tags: ['stations'],
    summary: 'Skip a station’s start-up setup',
    description: 'End the start-up setup of a station that is `setting_up` and open it for play now (ADR-0055, F-STN-26): the apps it ' +
        'has not installed yet install later, while it is idle, as they always have; an installer already running is let ' +
        'finish. Issued as `station.setup.skip` through the gateway; answers `202` with the command, which the console follows ' +
        'over the member’s `command.updated` to the station’s answer (`StationSetupSkipCommandResult`: skipped, or it was not ' +
        'setting up). Refused with `409` for a station that is offline or not setting up. Requires `stations:maintenance`. ' +
        'Audited (`stations.station.setup_skipped`).',
    [PERMISSION_EXTENSION]: STATIONS_MAINTENANCE,
    request: { params: StationActionParamsSchema },
    responses: {
        202: { description: 'The command was written and published.', content: { [JSON_CONTENT_TYPE]: { schema: StationCommandViewSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: stationNotFound,
        409: problemResponse('The station is offline or not setting up: there is no setup to skip.', 'conflict'),
        429: commonProblemResponses[429],
        500: commandUnavailable,
        503: commonProblemResponses[503],
    },
});
//# sourceMappingURL=floor.js.map